import type { AchievementDef, Content } from '@gra/content';
import { spentUpgradeRanks, type Profile } from './profile';
import type { GameState } from './types';

/**
 * Osiągnięcia: postęp liczony z profilu, a w trakcie wyprawy także z jej bieżącego stanu
 * (żeby „Osiągnięcie zdobyte” mogło wyskoczyć od razu). Po zakończeniu wyprawy, gdy rewardRun
 * doliczył ją już do profilu, wywołuj bez `run`, inaczej wyprawa policzy się podwójnie.
 */
export interface AchievementProgress {
  current: number;
  target: number;
}

export function achievementProgress(content: Content, profile: Profile, def: AchievementDef, run?: GameState): AchievementProgress {
  const st = profile.stats;
  const c = def.condition;
  switch (c.type) {
    case 'kills':
      return { current: st.totalKills + (run?.kills ?? 0), target: c.count };
    case 'runs':
      return { current: st.runs, target: c.count };
    case 'boss': {
      const now = run?.defeatedBosses.filter((id) => id === c.enemy).length ?? 0;
      return { current: (st.bossKills[c.enemy] ?? 0) + now, target: c.count };
    }
    case 'survive': {
      const best = c.zone ? (st.zoneBest[c.zone] ?? 0) : st.bestTime;
      const live = run && (!c.zone || run.zoneId === c.zone) ? run.time : 0;
      return { current: Math.floor(Math.max(best, live)), target: c.seconds };
    }
    case 'clear': {
      const classes = new Set(st.clears[c.zone] ?? []);
      if (run?.won && run.zoneId === c.zone) classes.add(run.classId);
      return c.allClasses ? { current: classes.size, target: Object.keys(content.classes).length } : { current: Math.min(1, classes.size), target: 1 };
    }
    case 'relics':
      return { current: st.relics + (run?.relics.length ?? 0), target: c.count };
    case 'level':
      return { current: Math.max(st.bestLevel, run?.level ?? 0), target: c.level };
    case 'pulls':
      return { current: st.pulls, target: c.count };
    case 'rarity': {
      // „Zdobądź epicki” zalicza też lepsze rzadkości.
      const min = content.rarityOrder.indexOf(c.rarity);
      return { current: profile.inventory.some((i) => content.rarityOrder.indexOf(i.rarity) >= min) ? 1 : 0, target: 1 };
    }
    case 'maxLevelItem':
      return { current: profile.inventory.some((i) => i.level >= content.game.gacha.maxLevel) ? 1 : 0, target: 1 };
    case 'talents':
      return { current: spentUpgradeRanks(profile), target: c.count };
    case 'gold':
      return { current: st.goldEarned + (run?.gold ?? 0), target: c.count };
  }
}

/** Oznacza spełnione osiągnięcia jako zdobyte. Zwraca tylko te zdobyte właśnie teraz. */
export function updateAchievements(content: Content, profile: Profile, run?: GameState): AchievementDef[] {
  const fresh: AchievementDef[] = [];
  for (const def of Object.values(content.achievements)) {
    if (profile.achievements[def.id]) continue;
    const p = achievementProgress(content, profile, def, run);
    if (p.current >= p.target) {
      profile.achievements[def.id] = 'done';
      fresh.push(def);
    }
  }
  return fresh;
}

/** Odbiór nagrody: kryształy, złoto; tytuł i ramka stają się dostępne do wyboru. */
export function claimAchievement(content: Content, profile: Profile, id: string): void {
  const def = content.achievements[id];
  if (!def) throw new Error(`Nieznane osiągnięcie: ${id}`);
  if (profile.achievements[id] !== 'done') throw new Error(`${def.name}: nagroda niedostępna`);
  profile.achievements[id] = 'claimed';
  profile.currency += def.reward.currency ?? 0;
  profile.gold += def.reward.gold ?? 0;
  // Pierwszy zdobyty tytuł i ramka zakładają się same.
  if (def.reward.title && !profile.title) profile.title = id;
  if (def.reward.frame && !profile.frame) profile.frame = id;
}

export function unclaimedAchievements(profile: Profile): number {
  return Object.values(profile.achievements).filter((v) => v === 'done').length;
}

/** Wybór tytułu albo ramki z odebranych nagród (null = bez). */
export function setTitle(content: Content, profile: Profile, id: string | null): void {
  if (id && (profile.achievements[id] !== 'claimed' || !content.achievements[id]?.reward.title)) throw new Error('Tytuł niedostępny');
  profile.title = id;
}

export function setFrame(content: Content, profile: Profile, id: string | null): void {
  if (id && (profile.achievements[id] !== 'claimed' || !content.achievements[id]?.reward.frame)) throw new Error('Ramka niedostępna');
  profile.frame = id;
}

export function activeTitle(content: Content, profile: Profile): string | null {
  return (profile.title && content.achievements[profile.title]?.reward.title) || null;
}

export function activeFrame(content: Content, profile: Profile): { name: string; color: string } | null {
  return (profile.frame && content.achievements[profile.frame]?.reward.frame) || null;
}
